import React, { useEffect, useState } from 'react';
import { Button, Badge, Alert, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import LoaderOverlay from '../../Components/shared/Loader';
import AlertService from '../../services/utility/alertService';
import { Formik } from 'formik';
import {
    getMyMasterRequest,
    getMasterRequestDetails,
    updateMasterRequestStatus
} from '../../services/api/masterRequestService';
import DynamicFields, { DetailTable, TypeBadge } from './DynamicFields';
import {
    ACTION_FIELDS,
    buildSchemaForFields,
    getEmptyValues,
    getFieldList,
    getStatusConfig,
    isMasterRequestReviewer,
    REQUEST_ACTIONS,
    REQUEST_STATUS
} from './masterRequestConfig';
import './MasterRequest.css';

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date)) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// One component serves the owner route and the reviewer route. isApproverView
// switches which endpoint is called; Stage 5 adds the action panel behind it.
function MasterRequestDetailPage({ isApproverView = false }) {
    const { id } = useParams();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState(null);
    const navigate = useNavigate();

    const backTo = isApproverView ? '/MasterRequestApprovalList' : '/MyMasterRequest';

    useEffect(() => {
        const fetchRequest = async () => {
            setLoading(true);
            try {
                const response = isApproverView
                    ? await getMasterRequestDetails(id)
                    : await getMyMasterRequest(id);
                setRequest(response.data || null);
            } catch (error) {
                console.error('Error loading request:', error);
                const status = error.response?.status;
                if (status === 404) {
                    AlertService.errorAlert('Request not found.');
                } else if (status === 403) {
                    AlertService.errorAlert('You do not have permission to view this request.');
                } else {
                    AlertService.errorAlert('Unable to load the request. Please try again.');
                }
                setRequest(null);
            } finally {
                setLoading(false);
            }
        };

        fetchRequest();
    }, [id, isApproverView]);

    // The route guard already blocks non-reviewers, but this page is shared with the
    // owner route, so the action panel checks the roles again before rendering.
    const canAct = isApproverView && isMasterRequestReviewer();

    const handleAction = async (values, { setSubmitting }) => {
        const isReject = action === REQUEST_ACTIONS.Reject;
        const confirmed = await AlertService.yesNoWarningAlert(
            isReject
                ? 'Reject this request? This cannot be undone.'
                : 'Mark this request as completed? This cannot be undone.'
        );

        if (!confirmed.isConfirmed) {
            setSubmitting(false);
            return;
        }

        setLoading(true);

        try {
            await updateMasterRequestStatus({
                RequestId: Number(id),
                Action: action,
                Comment: values.Comment || '',
                CreatedCode: values.CreatedCode || '',
                CreatedName: values.CreatedName || ''
            });

            // The loader overlay is position:fixed with a z-index above SweetAlert2's,
            // so it must come down BEFORE the modal is awaited - otherwise it sits on
            // top of the modal, blocks the OK button, the await never resolves, and
            // the loader is stuck on screen forever.
            setLoading(false);
            await AlertService.successAlert(
                isReject ? 'Request rejected.' : 'Request completed.'
            );
            navigate('/MasterRequestApprovalList');
        } catch (error) {
            console.error('Error updating request:', error);
            // The API returns the specific reason as a plain string for 400 and 403,
            // e.g. an already-closed request or a missing created code.
            const message = typeof error.response?.data === 'string'
                ? error.response.data
                : 'Unable to update the request. Please try again.';
            setLoading(false);
            AlertService.errorAlert(message);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const statusConfig = request ? getStatusConfig(request.REQ_STATUS) : null;
    const isOpen = request?.REQ_STATUS === REQUEST_STATUS.Open;

    // The submitted values, keyed the same way the form produced them, so the same
    // field definitions render the read-only view.
    const fields = request ? getFieldList(request.REQ_TYPE) : [];
    const options = request?.CUS_CODE
        ? { customers: [{ value: request.CUS_CODE, label: `${request.CUS_CODE} - ${request.CUS_NAME || ''}`.trim() }] }
        : { customers: [] };

    return (
        <div className="card aon-card master-request-page">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h4 className="mb-0">
                        Customer/Item Request #{id}
                        {statusConfig && (
                            <Badge bg={statusConfig.variant} className="ms-2 align-middle">
                                {statusConfig.label}
                            </Badge>
                        )}
                    </h4>
                    <Button variant="outline-primary" onClick={() => navigate(backTo)}>
                        Back to list
                    </Button>
                </div>
            </div>

            <div className="card-body aon-card-body">
                {!request && !loading ? (
                    <div className="text-center text-muted py-5">
                        This request is not available.
                    </div>
                ) : request ? (
                    <>
                        <DetailTable
                            items={[
                                { label: 'Request Type', display: <TypeBadge reqType={request.REQ_TYPE} /> },
                                { label: 'Status', display: <Badge bg={statusConfig.variant}>{statusConfig.label}</Badge> },
                                { label: 'Raised By', display: request.EMP_NAME },
                                { label: 'Raised On', display: formatDate(request.REQ_DATE) }
                            ]}
                        />

                        <hr className="my-3" />

                        <h6 className="mrq-section-title">Request Details</h6>
                        <DynamicFields
                            fields={fields}
                            values={request}
                            options={options}
                            readOnly
                        />

                        {isOpen ? (
                            canAct ? (
                                <>
                                    <hr className="my-3" />
                                    <h6 className="mrq-section-title">Review</h6>

                                    {action === null ? (
                                        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                                            <Button
                                                variant="danger"
                                                size="lg"
                                                onClick={() => setAction(REQUEST_ACTIONS.Reject)}
                                            >
                                                Reject Request
                                            </Button>
                                            <Button
                                                variant="success"
                                                size="lg"
                                                onClick={() => setAction(REQUEST_ACTIONS.Complete)}
                                            >
                                                Complete Request
                                            </Button>
                                        </div>
                                    ) : (
                                        <Formik
                                            key={action}
                                            initialValues={getEmptyValues(ACTION_FIELDS[action])}
                                            validationSchema={buildSchemaForFields(ACTION_FIELDS[action])}
                                            onSubmit={handleAction}
                                        >
                                            {({ values, errors, touched, handleSubmit, isSubmitting, setFieldValue, setFieldTouched }) => (
                                                <Form onSubmit={handleSubmit} noValidate>
                                                    <Alert
                                                        variant={action === REQUEST_ACTIONS.Reject ? 'danger' : 'success'}
                                                        className="py-2 small"
                                                    >
                                                        {action === REQUEST_ACTIONS.Reject
                                                            ? 'Rejecting this request. The engineer will see your reason.'
                                                            : 'Completing this request. Enter the details of the record you created.'}
                                                    </Alert>

                                                    <DynamicFields
                                                        fields={ACTION_FIELDS[action]}
                                                        values={values}
                                                        errors={errors}
                                                        touched={touched}
                                                        setFieldValue={setFieldValue}
                                                        setFieldTouched={setFieldTouched}
                                                    />

                                                    <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                                                        <Button
                                                            variant="outline-secondary"
                                                            type="button"
                                                            size="lg"
                                                            disabled={isSubmitting}
                                                            onClick={() => setAction(null)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant={action === REQUEST_ACTIONS.Reject ? 'danger' : 'success'}
                                                            type="submit"
                                                            size="lg"
                                                            disabled={isSubmitting}
                                                        >
                                                            {isSubmitting
                                                                ? 'Saving...'
                                                                : action === REQUEST_ACTIONS.Reject
                                                                    ? 'Confirm Rejection'
                                                                    : 'Confirm Completion'}
                                                        </Button>
                                                    </div>
                                                </Form>
                                            )}
                                        </Formik>
                                    )}
                                </>
                            ) : (
                                <Alert variant="info" className="mb-0 py-2 small">
                                    This request is awaiting review by the data admin.
                                </Alert>
                            )
                        ) : (
                            <>
                                <hr className="my-3" />
                                <h6 className="mrq-section-title">Admin Action</h6>

                                {request.REQ_STATUS === REQUEST_STATUS.Completed ? (
                                    <DetailTable
                                        items={[
                                            { label: 'Created Code', display: request.CREATED_CODE },
                                            { label: 'Created Name', display: request.CREATED_NAME },
                                            { label: 'Closed By', display: request.CLOSED_BY },
                                            { label: 'Closed On', display: formatDate(request.CLOSED_ON) }
                                        ]}
                                    />
                                ) : (
                                    <DetailTable
                                        items={[
                                            { label: 'Rejected By', display: request.CLOSED_BY },
                                            { label: 'Rejected On', display: formatDate(request.CLOSED_ON) }
                                        ]}
                                    />
                                )}

                                {request.ACTION_COMMENT && (
                                    <Alert
                                        variant={request.REQ_STATUS === REQUEST_STATUS.Completed ? 'success' : 'danger'}
                                        className="mb-0 py-2"
                                    >
                                        <div className="mrq-summary-label mb-1">
                                            {request.REQ_STATUS === REQUEST_STATUS.Completed ? 'Comment' : 'Reason'}
                                        </div>
                                        <div className="text-break">{request.ACTION_COMMENT}</div>
                                    </Alert>
                                )}
                            </>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}

export default MasterRequestDetailPage;
